import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines multiple class names using clsx and tailwind-merge
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes an API response into an array.
 * Handles paginated wrappers ({ data: [...] }), bare arrays, and null/undefined.
 */
export function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as any).data)) {
    return (data as any).data as T[];
  }
  return [];
}

/**
 * Generates a random string
 */
export function generateRandomString(length = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Debounces a function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait = 300
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttles a function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit = 300
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  
  return function(...args: Parameters<T>): void {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Removes Symbol values from an object to make it cloneable
 */
export function removeSymbols<T>(obj: T): T {
  if (!obj || typeof obj !== 'object') return obj;
  
  try {
    return JSON.parse(JSON.stringify(obj, (key, value) => 
      typeof value === 'symbol' ? undefined : value
    ));
  } catch (error) {
    console.error('Error removing symbols from object:', error);
    
    if (Array.isArray(obj)) {
      return obj.map(item => removeSymbols(item)) as unknown as T;
    }
    
    const result: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = (obj as Record<string, any>)[key];
        if (typeof value !== 'symbol') {
          result[key] = typeof value === 'object' && value !== null 
            ? removeSymbols(value) 
            : value;
        }
      }
    }
    return result as T;
  }
}